import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { UserCircleIcon, KeyIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const profileSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  phone: z.string().optional(),
});

const passwordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[a-zA-Z]/, 'Password must contain at least one letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type ProfileForm = z.infer<typeof profileSchema>;
type PasswordForm = z.infer<typeof passwordSchema>;

export function Profile() {
  const { user, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'profile' | 'password'>('profile');

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors, isSubmitting: isProfileSubmitting },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isPasswordSubmitting },
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const onSubmitProfile = async (data: ProfileForm) => {
    try {
      const response = await authApi.updateProfile(data);
      updateUser(response.data.data.user);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  const onSubmitPassword = async (data: PasswordForm) => {
    try {
      await authApi.changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });
      resetPassword();
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error('Failed to change password');
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-bold text-secondary-900 mb-6">My Profile</h1>

      {/* Tabs */}
      <div className="border-b border-secondary-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('profile')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'profile'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700'
            }`}
          >
            <UserCircleIcon className="h-5 w-5 mr-2" />
            Profile
          </button>
          <button
            onClick={() => setActiveTab('password')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
              activeTab === 'password'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-secondary-500 hover:text-secondary-700'
            }`}
          >
            <KeyIcon className="h-5 w-5 mr-2" />
            Password
          </button>
        </nav>
      </div>

      {/* Profile Form */}
      {activeTab === 'profile' && (
        <div className="card p-6">
          <div className="flex items-center mb-6">
            <div className="h-20 w-20 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-2xl font-bold text-primary-700">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </span>
            </div>
            <div className="ml-6">
              <p className="text-lg font-semibold text-secondary-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-sm text-secondary-500">{user?.email}</p>
              <p className="text-xs text-secondary-400 capitalize">{user?.role?.toLowerCase()}</p>
            </div>
          </div>

          <form onSubmit={handleSubmitProfile(onSubmitProfile)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input {...registerProfile('firstName')} type="text" className="input" />
                {profileErrors.firstName && (
                  <p className="text-sm text-red-600 mt-1">{profileErrors.firstName.message}</p>
                )}
              </div>
              <div>
                <label className="label">Last Name</label>
                <input {...registerProfile('lastName')} type="text" className="input" />
                {profileErrors.lastName && (
                  <p className="text-sm text-red-600 mt-1">{profileErrors.lastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label className="label">Email</label>
              <input type="email" value={user?.email || ''} disabled className="input bg-secondary-50" />
              <p className="text-xs text-secondary-500 mt-1">Email cannot be changed</p>
            </div>

            <div>
              <label className="label">Phone</label>
              <input {...registerProfile('phone')} type="tel" className="input" />
            </div>

            <div className="pt-4">
              <button type="submit" disabled={isProfileSubmitting} className="btn-primary">
                {isProfileSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Password Form */}
      {activeTab === 'password' && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-secondary-900 mb-4">Change Password</h3>
          <form onSubmit={handleSubmitPassword(onSubmitPassword)} className="space-y-4">
            <div>
              <label className="label">Current Password</label>
              <input
                {...registerPassword('currentPassword')}
                type="password"
                className="input"
              />
              {passwordErrors.currentPassword && (
                <p className="text-sm text-red-600 mt-1">
                  {passwordErrors.currentPassword.message}
                </p>
              )}
            </div>

            <div>
              <label className="label">New Password</label>
              <input {...registerPassword('newPassword')} type="password" className="input" />
              {passwordErrors.newPassword && (
                <p className="text-sm text-red-600 mt-1">{passwordErrors.newPassword.message}</p>
              )}
            </div>

            <div>
              <label className="label">Confirm New Password</label>
              <input
                {...registerPassword('confirmPassword')}
                type="password"
                className="input"
              />
              {passwordErrors.confirmPassword && (
                <p className="text-sm text-red-600 mt-1">
                  {passwordErrors.confirmPassword.message}
                </p>
              )}
            </div>

            <div className="pt-4">
              <button type="submit" disabled={isPasswordSubmitting} className="btn-primary">
                {isPasswordSubmitting ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
